<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { compare, prettyHtml, formatMarkdown } from './helpers/textDiffHelper';

import { OnClickOutside } from '@vueuse/components';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import Button from 'next/button/Button.vue';
import KnowledgePanel from './components/KnowledgePanel.vue';
import ResetSessionDialog from './components/ResetSessionDialog.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const uiFlags = useMapGetter('wgptBotKnowledgeManager/getUIFlags');
const messages = useMapGetter('wgptBotKnowledgeManager/getMessages');
const knowledge = useMapGetter('wgptBotKnowledgeManager/getKnowledge');
const currentKnowledge = useMapGetter(
  'wgptBotKnowledgeManager/getCurrentKnowledge'
);

const resetSessionDialog = ref(null);
const showMenu = ref(false);

const botId = computed(() => Number(route.params.botId));

const hasPendingChanges = computed(() => {
  return knowledge.value !== currentKnowledge.value;
});

const hasAnyMessages = computed(() => {
  return messages.value && messages.value.length > 0;
});

const diffHtml = computed(() => {
  const original = knowledge.value || '';
  const current = currentKnowledge.value || '';
  if (!original && !current) {
    return t(
      'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.OUTPUT_SECTION.PLACEHOLDER'
    );
  }

  if (original === current) {
    return formatMarkdown(current);
  }

  const diffArray = compare(original, current);
  return prettyHtml(diffArray);
});

const actionsDisabled = computed(() => {
  return (
    !hasPendingChanges.value ||
    uiFlags.value.isFetching ||
    uiFlags.value.isUpdating ||
    uiFlags.value.isSending
  );
});

const menuItems = computed(() => {
  return [
    {
      label: 'New session',
      value: 'reset',
      action: 'reset',
      icon: 'i-lucide-refresh-ccw',
      disabled: !hasAnyMessages.value,
    },
  ];
});

const handleSave = async () => {
  try {
    await store.dispatch('wgptBotKnowledgeManager/persist', botId.value);
  } catch (error) {
    useAlert(
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.OUTPUT_SECTION.ERROR_MESSAGE')
    );
  }
};

const handleMenuAction = ({ action }) => {
  showMenu.value = false;
  if (action === 'reset') {
    resetSessionDialog.value.open();
  }
};
</script>

<template>
  <KnowledgePanel
    :diff-html="diffHtml"
    :is-fetching="uiFlags.isFetching"
    :is-available="uiFlags.isAvailable"
  >
    <template #actions>
      <Button
        :label="
          t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.OUTPUT_SECTION.SAVE_BUTTON')
        "
        sm
        slate
        ghost
        class="!rounded-e-none"
        :disabled="actionsDisabled"
        :is-loading="uiFlags.isUpdating"
        @click="handleSave"
      />
      <div class="flex-shrink-0 w-px h-3.5 mx-1.5 bg-white/30" />
      <div class="relative">
        <OnClickOutside @trigger="showMenu = false">
          <Button
            icon="i-lucide-chevron-down"
            sm
            slate
            ghost
            class="ltr:rounded-l-none rtl:rounded-r-none"
            @click.stop="showMenu = !showMenu"
          />
          <DropdownMenu
            v-if="showMenu"
            :menu-items="menuItems"
            class="mt-2 ltr:right-0 rtl:left-0 top-full"
            @action="handleMenuAction($event)"
          />
        </OnClickOutside>
      </div>
    </template>

    <ResetSessionDialog ref="resetSessionDialog" />
  </KnowledgePanel>
</template>
